import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// R15 T2/T3 - the authorised UI route to the READ-ONLY task diagnostic and to the dedicated, strictly scoped task-only soft cancel.
// Nothing here changes a task until a manifest has been built, specific rows are ticked, a written reason is given and the exact
// phrase is typed. Mirrors backend/task_classification.REPORT_CATEGORIES and task_cleanup.CLEANUP_CLASSES (kept in step on purpose).
// R17: the keys MUST be exactly backend/task_classification.REPORT_CATEGORIES. The UI used to offer stage_departed_obsolete / needs_review / genuine_work, which the backend does not know - it ignored the
// unknown value and returned EVERY task (all 14,633 rows). A backend test now reads this very list and compares it to the backend tuple, and the page also loads the live catalogue (labels + safety copy)
// from /admin/task-integrity/report-categories; this static list is only the fallback until it arrives.
export const REPORT_CATEGORIES = [
  { key: "completed_with_evidence", label: "Completed - proven by a linked record" },
  { key: "superseded_satisfied_later", label: "Overtaken - a later governed event satisfied it" },
  { key: "duplicate_superseded", label: "Duplicate - an authoritative task is already kept" },
  { key: "system_failure_resolved_by_later_success", label: "Alert resolved - a later success followed the failure" },
  { key: "still_valid", label: "Retained - no evidence it is obsolete" },
  { key: "manual_review_required", label: "Needs a person - SalesOS cannot tell" },
];
export const CONFIDENCE_LABELS = {
  evidence: "Proven by a linked record",
  positive: "Positive evidence it is still wanted",
  default: "No evidence either way (default)",
  review: "A person must decide",
};
export const categoryLabel = (key, catalogue = REPORT_CATEGORIES) => (catalogue.find((c) => c.key === key) || {}).label || key;
export const CLEANUP_CLASSES = { duplicate: "Duplicate of an authoritative active task", stage_departed: "Governing stage already left", pursuit_closed_automatic: "Automatic task on a closed pursuit",
  contact_objective_reached: "Automatic 'reach the customer' task - the customer was reached by a later connected call (opt-in)",
  redundant_automatic_chase: "Redundant overdue automatic chase — keep the earliest overdue commitment (opt-in)" };
export const OPT_IN_CLASS = "contact_objective_reached";
export const PAGE = 100;

export const applyPhrase = (n, manifestId) => `CANCEL ${n} TASKS ${manifestId}`;
export const rollbackPhrase = (runId) => `ROLLBACK ${runId}`;

// Pulls every page of the CSV by following X-Next-Offset with the first page's snapshot, so the file is complete (not "the first N rows").
// The data fingerprint is compared across pages: a different value means tasks changed while paging and the file is flagged, not trusted.
export async function downloadCompleteCsv(params, { get = (url, cfg) => api.get(url, cfg) } = {}) {
  let offset = 0;
  let snapshot = null;
  let fingerprint = null;
  let consistent = true;
  let total = null;
  const chunks = [];
  for (let guard = 0; guard < 1000; guard += 1) {
    const qs = new URLSearchParams({ ...params, offset: String(offset) });
    if (snapshot) qs.set("snapshot_at", snapshot);
    const res = await get(`/admin/task-integrity/backlog-preview.csv?${qs.toString()}`, { responseType: "text", transformResponse: (d) => d });
    const h = res.headers || {};
    snapshot = snapshot || h["x-snapshot-at"];
    total = h["x-rows-total"] ?? total;
    const fp = h["x-data-fingerprint"];
    if (fingerprint && fp && fp !== fingerprint) consistent = false;
    fingerprint = fingerprint || fp;
    // later pages repeat the header row; keep it once
    chunks.push(chunks.length === 0 ? res.data : res.data.split("\n").slice(1).join("\n"));
    const next = h["x-next-offset"];
    if (next === undefined || next === null || next === "") return { csv: chunks.join(""), pages: chunks.length, total: Number(total), consistent, snapshot };
    offset = Number(next);
  }
  throw new Error("Export did not finish (page guard reached)");
}

const Coverage = ({ cov, totals }) => (
  <div data-testid="tc-coverage" className="text-xs rounded border bg-muted/40 px-3 py-2 space-y-0.5">
    <div>
      Showing rows <strong>{cov.rows_total ? cov.offset + 1 : 0}</strong>–<strong>{cov.offset + cov.rows_returned}</strong> of <strong>{cov.rows_total}</strong>
      {cov.category_filter ? <> in <code>{cov.category_filter}</code></> : null}. Snapshot <code>{String(cov.snapshot_at)}</code>.
    </div>
    <div>Scope: {cov.scope}. TEST records: {cov.include_test ? "included" : `excluded (${cov.test_excluded} hidden)`}.</div>
    {cov.truncated && <div data-testid="tc-truncated" className="font-medium text-[hsl(var(--warning))]">This is a page, not the whole set — use Next, or “Download complete CSV”.</div>}
    {totals && <div>Active tasks classified: {totals.active_classified}.</div>}
  </div>
);

const BacklogPreview = () => {
  const [category, setCategory] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [offset, setOffset] = useState(0);
  const [snapshot, setSnapshot] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dl, setDl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catalogue, setCatalogue] = useState(REPORT_CATEGORIES);
  useEffect(() => { api.get("/admin/task-integrity/report-categories").then((r) => { if (Array.isArray(r.data?.categories) && r.data.categories.length) setCatalogue(r.data.categories); }).catch(() => {}); }, []);

  const load = useCallback(async (off, snap) => {
    setError(null);
    try {
      const qs = new URLSearchParams({ include_test: String(includeTest), page: "1", page_size: String(PAGE) });
      if (category) qs.set("category", category);
      // the paged preview endpoint is page-based; the offset is a multiple of PAGE so page = offset / PAGE + 1
      qs.set("page", String(Math.floor(off / PAGE) + 1));
      if (snap) qs.set("snapshot_at", snap);
      const { data: d } = await api.get(`/admin/task-integrity/backlog-preview?${qs.toString()}`);
      setData(d);
      setSnapshot(d.coverage.snapshot_at);
    } catch (e) { setError(errDetail(e, "Could not load the preview")); setData(null); }
  }, [category, includeTest]);

  useEffect(() => { setOffset(0); setSnapshot(null); load(0, null); }, [load]);

  const go = (off) => { setOffset(off); load(off, snapshot); };

  const download = async () => {
    setBusy(true); setDl(null);
    try {
      const params = { include_test: String(includeTest), limit: "20000" };
      if (category) params.category = category;
      const r = await downloadCompleteCsv(params);
      const url = URL.createObjectURL(new Blob([r.csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "task_backlog_preview.csv"; a.click();
      URL.revokeObjectURL(url);
      setDl(r);
      if (!r.consistent) toast.warning("Tasks changed while the file was being fetched — the pages are not one consistent set. Download again.");
    } catch (e) { toast.error(errDetail(e, "Download failed")); } finally { setBusy(false); }
  };

  const cov = data?.coverage;
  return (
    <Card data-testid="tc-preview">
      <CardHeader><CardTitle className="text-base font-heading">Task backlog — read-only preview</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Category</Label>
            <select data-testid="tc-category" className="h-9 rounded border bg-background px-2 text-sm block" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All categories</option>
              {catalogue.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-1.5 text-xs">
            <input data-testid="tc-include-test" type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} /> Include TEST records
          </label>
          <Button data-testid="tc-download" size="sm" variant="outline" disabled={busy} onClick={download}>{busy ? "Fetching all pages…" : "Download complete CSV"}</Button>
        </div>
        {error && <p data-testid="tc-error" className="text-sm text-[hsl(var(--danger))]">{error}</p>}
        {dl && <p data-testid="tc-dl-result" className="text-xs text-muted-foreground">Downloaded {dl.total} rows in {dl.pages} request{dl.pages === 1 ? "" : "s"}{dl.consistent ? "" : " — NOT consistent, tasks changed while paging"}.</p>}
        {cov && <Coverage cov={cov} totals={data.totals} />}
        {data && !category && (
          <div className="overflow-x-auto" data-testid="tc-category-summary">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-muted-foreground"><th className="pr-2">Category</th><th className="pr-2 text-right">Tasks</th><th className="pr-2 text-right">Customers</th><th>Why (basis x confidence)</th></tr></thead>
              <tbody>
                {catalogue.map((c) => {
                  const e = data.categories?.[c.key];
                  if (!e) return null;
                  return (
                    <tr key={c.key} className="border-t align-top" data-testid={`tc-summary-${c.key}`}>
                      <td className="pr-2">{c.label}</td><td className="pr-2 text-right tabular-nums">{e.unique_task_count}</td><td className="pr-2 text-right tabular-nums">{e.unique_customer_count}</td>
                      <td className="text-muted-foreground">{Object.entries(e.by_confidence || {}).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${CONFIDENCE_LABELS[k] || k}`).join("; ") || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground pt-1">"Retained" is the classifier's default: a task lands there when nothing proves it done, duplicated or overtaken. It is NOT a finding that the task is genuine work.</p>
          </div>
        )}
        {data && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="tc-rows">
              <thead><tr className="text-left text-muted-foreground"><th className="pr-2">Task</th><th className="pr-2">Title</th><th className="pr-2">Category</th><th className="pr-2">Basis</th><th className="pr-2">Queue</th><th>Reason</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.task_id} className="border-t align-top">
                    <td className="pr-2 font-mono">{r.task_id}</td><td className="pr-2">{r.title}</td>
                    <td className="pr-2"><Badge variant="outline">{categoryLabel(r.report_category, catalogue)}</Badge></td>
                    <td className="pr-2" title={CONFIDENCE_LABELS[r.report_confidence] || ""}>{(r.report_basis || "").replaceAll("_", " ")}{r.report_confidence ? ` (${r.report_confidence})` : ""}</td><td className="pr-2">{r.queue}</td><td>{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.rows.length === 0 && <p data-testid="tc-empty" className="text-sm text-muted-foreground py-2">No tasks in this view.</p>}
          </div>
        )}
        {cov && (
          <div className="flex gap-2">
            <Button data-testid="tc-prev" size="sm" variant="outline" disabled={offset === 0} onClick={() => go(Math.max(0, offset - PAGE))}>Previous</Button>
            <Button data-testid="tc-next" size="sm" variant="outline" disabled={cov.next_offset === null} onClick={() => go(cov.next_offset)}>Next</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CleanupFlow = ({ onMutation }) => {
  const [manifest, setManifest] = useState(null);
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState({});
  const [reason, setReason] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [run, setRun] = useState(null);
  const [runs, setRuns] = useState([]);
  const [rbReason, setRbReason] = useState("");
  const [rbPhrase, setRbPhrase] = useState("");

  const loadRuns = useCallback(() => { api.get("/admin/task-integrity/cleanup/runs").then((r) => setRuns(r.data || [])).catch(() => setRuns([])); }, []);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  const [optIn, setOptIn] = useState(false);          // R17 review F3: the reached-customer class is never in a default manifest; building one for it is a separate, explicit choice
  const [consolidate, setConsolidate] = useState(false);
  const [chasePreview, setChasePreview] = useState(null);
  const invalidateReview = () => {
    setManifest(null); setItems([]); setChasePreview(null); setPicked({}); setPhrase("");
    onMutation?.();
  };
  const previewChases = async () => {
    setBusy(true); setChasePreview(null);
    try { const { data } = await api.get("/admin/task-integrity/consolidation-preview"); setChasePreview(data); }
    catch (e) { toast.error(errDetail(e, "Could not load consolidation preview")); }
    finally { setBusy(false); }
  };
  const build = async () => {
    setBusy(true); setRun(null);
    try {
      const { data: m } = await api.post("/admin/task-integrity/cleanup/manifest", { include_test: false, ...(consolidate ? { classes: ["redundant_automatic_chase"] } : optIn ? { classes: [OPT_IN_CLASS] } : {}) });
      const { data: full } = await api.get(`/admin/task-integrity/cleanup/manifests/${m.manifest_id}?page_size=500`);
      setManifest(m); setItems(full.items || []); setPicked({}); setPhrase("");
    } catch (e) { toast.error(errDetail(e, "Could not build the manifest")); } finally { setBusy(false); }
  };

  const ids = Object.keys(picked).filter((k) => picked[k]);
  const expected = manifest ? applyPhrase(ids.length, manifest.manifest_id) : "";
  const canApply = !busy && manifest && ids.length > 0 && reason.trim().length >= 10 && phrase === expected;

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/task-integrity/cleanup/apply", { manifest_id: manifest.manifest_id, task_ids: ids, reason, confirmation_phrase: phrase });
      setRun(data); invalidateReview(); loadRuns();
      toast.success(`Cancelled ${data.counts?.applied ?? 0}, rejected ${data.counts?.rejected ?? 0}`);
    } catch (e) { toast.error(errDetail(e, "Apply failed")); } finally { setBusy(false); }
  };

  const rollback = async (runId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/task-integrity/cleanup/rollback", { run_id: runId, reason: rbReason, confirmation_phrase: rbPhrase });
      toast.success(`Restored ${data.rollback?.restored?.length ?? data.rollback?.restored ?? 0}, skipped (changed since) ${data.rollback?.skipped_changed_since?.length ?? data.rollback?.skipped_changed_since ?? 0}`); setRbPhrase(""); setRun(null); invalidateReview(); loadRuns();
    } catch (e) { toast.error(errDetail(e, "Rollback failed")); } finally { setBusy(false); }
  };

  const resume = async (runId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/task-integrity/cleanup/runs/${runId}/resume`);
      toast.success(`Run resumed: cancelled ${data.counts?.applied ?? 0}, rejected ${data.counts?.rejected ?? 0}`); setRun(data); invalidateReview(); loadRuns();
    } catch (e) { toast.error(errDetail(e, "Resume failed")); } finally { setBusy(false); }
  };

  const counts = items.reduce((a, i) => ({ ...a, [i.cleanup_class]: (a[i.cleanup_class] || 0) + 1 }), {});
  return (
    <Card data-testid="tc-cleanup">
      <CardHeader><CardTitle className="text-base font-heading">Task cleanup — strictly scoped soft cancel</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground text-xs">
          Building a manifest saves a review record but does not change tasks. Applying cancels only the tasks you tick (never completes, never deletes), re-checks every task first, and can be rolled back.
          Worked, reviewed, approval-pending and ambiguous tasks are never offered.
        </p>
        <label className="flex items-center gap-1.5 text-xs" data-testid="tc-optin-label">
          <input data-testid="tc-optin" type="checkbox" checked={optIn} onChange={(e) => { setOptIn(e.target.checked); setConsolidate(false); setManifest(null); setPicked({}); setPhrase(""); }} />
          Build for ONLY the opt-in class: {CLEANUP_CLASSES[OPT_IN_CLASS]}
        </label>
        <label className="flex items-center gap-1.5 text-xs">
          <input data-testid="tc-consolidate" type="checkbox" checked={consolidate} onChange={(e) => { setConsolidate(e.target.checked); setOptIn(false); setManifest(null); setPicked({}); setPhrase(""); }} />
          Build ONLY redundant overdue automatic chases. Keep the earliest overdue task; protect callbacks, worked tasks and other products. Requires transaction support.
        </label>
        <Button data-testid="tc-chase-preview" variant="outline" size="sm" disabled={busy} onClick={previewChases}>Review repeated overdue chases (read-only)</Button>
        {chasePreview && <div data-testid="tc-chase-preview-result" className="text-xs border rounded p-2">
          {chasePreview.total} redundant task(s); {chasePreview.keeper_ids?.length ?? 0} retained task(s). No tasks changed; no manifest created.
          {(chasePreview.items || []).slice(0, 50).map((i) => <div key={i.task_id}>Cancel candidate {i.task_id}; retain {i.keeper_task_id}; original due {String(i.due_at)}; retained due {String(i.evidence?.keeper_due_at)}.</div>)}
          {(chasePreview.items || []).length > 50 && <p>Showing the first 50 candidates. Review the bounded manifest before applying.</p>}
        </div>}
        <Button data-testid="tc-build" size="sm" disabled={busy} onClick={build}>{optIn || consolidate ? "Build manifest for the opt-in class (preview only)" : "Build manifest (preview only)"}</Button>
        {manifest && (
          <div className="space-y-2">
            <div data-testid="tc-manifest-head" className="text-xs rounded border bg-muted/40 px-3 py-2">
              Manifest <code>{manifest.manifest_id}</code> — {items.length} candidate task(s). {Object.entries(counts).map(([k, n]) => `${CLEANUP_CLASSES[k] || k}: ${n}`).join(" · ") || "Nothing qualifies."}
            </div>
            <div className="max-h-72 overflow-auto">
              {items.map((i) => (
                <label key={i.task_id} className="flex items-start gap-2 text-xs border-t py-1">
                  <input data-testid={`tc-pick-${i.task_id}`} type="checkbox" checked={!!picked[i.task_id]} onChange={(e) => setPicked((p) => ({ ...p, [i.task_id]: e.target.checked }))} />
                  <span><span className="font-mono">{i.task_id}</span> — {i.title} <Badge variant="outline">{CLEANUP_CLASSES[i.cleanup_class] || i.cleanup_class}</Badge> <span>Customer {i.scope?.customer_id || i.scope?.contact_id || "not recorded"}; product {i.scope?.product_code || "not recorded"}; owner {i.owner_name || i.owner_user_id || "unassigned"}. </span><span className="text-muted-foreground">{i.reason}</span>{i.keeper_task_id && <span> Retain {i.keeper_task_id}; original due {String(i.due_at)}; retained due {String(i.evidence?.keeper_due_at ?? "see retained task")}.</span>}</span>
                </label>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Reason (written to the audit trail)</Label>
              <Input data-testid="tc-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
              <Label className="text-xs">Type the exact phrase for {ids.length} selected task(s)</Label>
              <p className="text-xs font-mono bg-muted px-2 py-1 rounded" data-testid="tc-expected">{expected}</p>
              <Input data-testid="tc-phrase" value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={expected} />
              <Button data-testid="tc-apply" size="sm" disabled={!canApply} onClick={apply}>{busy ? "Working…" : `Cancel ${ids.length} selected task(s)`}</Button>
            </div>
          </div>
        )}
        {run && <p data-testid="tc-run-result" className="text-xs">Run <code>{run.run_id}</code>: applied {run.counts?.applied}, rejected {run.counts?.rejected}.</p>}
        {runs.length > 0 && (
          <div className="space-y-1" data-testid="tc-runs">
            <Label className="text-xs">Recent cleanup runs</Label>
            {runs.slice(0, 5).map((r) => (
              <div key={r.run_id} className="flex flex-wrap items-center gap-2 text-xs border-t py-1">
                <code>{r.run_id}</code><span>{r.counts?.applied ?? 0} cancelled</span><Badge variant="outline">{r.rolled_back_at ? "rolled back" : r.status}</Badge>
                {r.status === "recoverable" && (
                  <Button size="sm" variant="outline" data-testid={`tc-resume-${r.run_id}`} disabled={busy} onClick={() => resume(r.run_id)}>Resume interrupted run</Button>
                )}
                {!r.rolled_back_at && (r.counts?.applied ?? 0) > 0 && (
                  <>
                    <Input className="h-7 w-48 text-xs" placeholder="Rollback reason" value={rbReason} onChange={(e) => setRbReason(e.target.value)} />
                    <Input data-testid={`tc-rb-phrase-${r.run_id}`} className="h-7 w-56 text-xs" placeholder={rollbackPhrase(r.run_id)} value={rbPhrase} onChange={(e) => setRbPhrase(e.target.value)} />
                    <Button size="sm" variant="outline" disabled={busy || rbPhrase !== rollbackPhrase(r.run_id) || !rbReason.trim()} onClick={() => rollback(r.run_id)}>Roll back</Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const PROPOSAL_LABELS = { satisfied_by_later_record: "Objective already satisfied by a later record", surplus_sibling: "Additional task of the same pursuit and purpose", keeper: "Retained task (earliest overdue commitment for consolidation)",
  worked: "Worked by a person - never proposed", unknown_purpose: "Purpose unknown - never proposed" };

// R17 - READ-ONLY cluster analysis of the call / follow-up backlog. It proposes nothing and writes nothing: it shows which tasks are one pursuit's pile-up, who produced them, and which already have a LATER record
// that satisfied their objective. The exact keeper / candidate ids are in the complete CSV; a cleanup is a separate, manifest-gated, human-approved write below.
export const ClusterSummary = () => {
  const [rep, setRep] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setRep(null); setError(null);
    api.get("/admin/task-integrity/clusters?page_size=1")
      .then((r) => { if (r.data && r.data.totals && r.data.totals.by_proposal) setRep(r.data); else setError("The cluster analysis answered with an unexpected response."); })
      .catch((e) => setError(errDetail(e, "Could not load the cluster analysis")));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Card data-testid="tc-clusters">
      <CardHeader><CardTitle className="text-base font-heading">Call / follow-up backlog — clusters (read-only)</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-xs">
        {error ? <p role="alert" data-testid="tc-clusters-error">{error} <Button size="sm" variant="outline" onClick={load}>Try again</Button></p> : !rep ? <p className="text-muted-foreground">Loading…</p> : (
          <>
            <p data-testid="tc-clusters-totals"><b>{rep.totals.tasks}</b> active call / follow-up / booking-chase task(s) in <b>{rep.totals.clusters}</b> cluster(s) (one pursuit x one purpose). Retained or keepers: <b>{rep.totals.keepers_or_retained}</b>.
              <b> Proposals (information only): {rep.totals.proposals_informational ?? rep.totals.candidates_for_reviewed_cleanup}</b> ({rep.totals.candidates_satisfied_by_later_record} satisfied by a later record, {rep.totals.candidates_surplus_sibling} surplus sibling).</p>
            {rep.totals.executable_by_the_real_cleanup !== undefined && (
              <p data-testid="tc-clusters-executable" className="rounded border bg-muted/40 px-2 py-1">
                <b>Executable by the real cleanup today: {rep.totals.executable_by_the_real_cleanup}</b>
                {Object.entries(rep.totals.executable_by_class || {}).map(([k, v]) => ` · ${v} ${CLEANUP_CLASSES[k] || k}`).join("")}.
                <b> Not executable: {rep.totals.proposals_not_executable}</b> - a proposal is not a cleanup; only a task the cleanup rules accept can enter a manifest.
                {Object.entries(rep.totals.not_executable_reasons || {}).slice(0, 4).map(([k, v]) => <span key={k} className="block text-muted-foreground">{v} x {k}</span>)}
              </p>
            )}
            <div className="grid sm:grid-cols-2 gap-x-4">
              <ul data-testid="tc-clusters-proposals">{Object.entries(rep.totals.by_proposal).map(([k, v]) => <li key={k} className="flex justify-between"><span>{PROPOSAL_LABELS[k] || k}</span><span className="font-mono">{v}</span></li>)}</ul>
              <ul data-testid="tc-clusters-producers">{Object.entries(rep.totals.by_producer).map(([k, v]) => <li key={k} className="flex justify-between"><span>Produced by {k.replaceAll("_", " ")}</span><span className="font-mono">{v}</span></li>)}</ul>
            </div>
            <p className="text-muted-foreground">{rep.interpretation}</p>
            <a href={`${backendUrl}/api/admin/task-integrity/clusters.csv`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid="tc-clusters-csv">Download the complete cluster CSV (exact keeper / candidate ids)</Button></a>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export function TaskCleanupTab() {
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((r) => r + 1), []);
  return (
    <div className="space-y-4" data-testid="task-cleanup-tab">
      <BacklogPreview key={`backlog-${revision}`} />
      <ClusterSummary key={`clusters-${revision}`} />
      <CleanupFlow onMutation={refresh} />
    </div>
  );
}
